import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { Filter, Loader2 } from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { 
  BrutalSelect,
  BrutalSelectTrigger,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectValue,
} from '@/components/ui/BrutalSelect';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { EventCard } from '@/components/events/EventCard';
import { useEventsSearch } from '@/hooks/useEventsSearch';
import { useEventCities } from '@/hooks/useEventCities';

const Events = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    format: 'all',
    city: 'all',
    search: searchParams.get('search') || '',
  });

  // Sync URL search param with filters
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch && urlSearch !== filters.search) {
      setFilters(prev => ({ ...prev, search: urlSearch }));
    }
  }, [searchParams]);

  const { data: events = [], isLoading, isFetching } = useEventsSearch(filters);
  const { data: cities = [] } = useEventCities();

  // Check if any filters are active
  const hasActiveFilters = 
    filters.type !== 'all' || 
    filters.format !== 'all' || 
    filters.city !== 'all' || 
    filters.search !== '';

  const resetFilters = () => {
    setFilters({
      type: 'all',
      format: 'all',
      city: 'all',
      search: '',
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={t("events.seoTitle")}
        description={t("events.seoDescription")}
        type="website"
      />
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <SEOBreadcrumb items={[{ label: t('common.events') }]} />
            <h1 className="font-heading font-bold text-4xl md:text-6xl text-dark mb-4 flex items-center justify-center gap-3">
              {t('events.heroTitle')}
              {isFetching && !isLoading && (
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              )}
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('events.heroSubtitle')}
            </p>
          </div>

        {/* Mobile Filter Button */}
        <div className="mb-4 md:hidden">
          <BrutalButton
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            variant="outline"
            className="w-full"
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('events.filters')}
          </BrutalButton>
        </div>

        {/* Filters */}
        <div className={`grid md:grid-cols-4 gap-4 mb-8 ${showMobileFilters ? 'grid' : 'hidden md:grid'}`}>
          <BrutalInput
            placeholder={t('events.search')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <BrutalSelect value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t('events.allTypes')} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              <BrutalSelectItem value="all">{t('events.allTypes')}</BrutalSelectItem>
              <BrutalSelectItem value="free">{t('events.free')}</BrutalSelectItem>
              <BrutalSelectItem value="paid_external">{t('events.paid')}</BrutalSelectItem>
            </BrutalSelectContent>
          </BrutalSelect>
          <BrutalSelect value={filters.format} onValueChange={(v) => setFilters({ ...filters, format: v })}>
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t('events.allFormats')} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              <BrutalSelectItem value="all">{t('events.allFormats')}</BrutalSelectItem>
              <BrutalSelectItem value="in_person">{t('events.inPerson')}</BrutalSelectItem>
              <BrutalSelectItem value="online">{t('events.online')}</BrutalSelectItem>
              <BrutalSelectItem value="hybrid">{t('events.hybrid')}</BrutalSelectItem>
            </BrutalSelectContent>
          </BrutalSelect>
          <BrutalSelect value={filters.city} onValueChange={(v) => setFilters({ ...filters, city: v })}>
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t('events.allCities')} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              <BrutalSelectItem value="all">{t('events.allCities')}</BrutalSelectItem>
              {cities.map((city) => (
                <BrutalSelectItem key={city} value={city}>{city}</BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>

        {/* Events Grid */}
        {isLoading && events.length === 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-96 border-[3px] border-dark bg-muted animate-pulse shadow-[6px_6px_0px_0px] shadow-dark" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-dark/70 font-body text-lg">{t('events.noEvents')}</p>
            {hasActiveFilters && (
              <BrutalButton 
                variant="outline" 
                size="sm" 
                className="mt-4"
                onClick={resetFilters}
              >
                {t('events.resetFilters')}
              </BrutalButton>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {events.map((event) => (
              <EventCard key={event.id} data={event} />
            ))}
          </div>
        )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Events;
